import AppShell from '@/components/AppShell'
import { Link } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { fetchLoads } from '@/lib/mockApi'
import type { LoadTicket } from '@/lib/types'
import Sparkline from '@/components/Sparkline'
import { get } from '@/lib/storage'

type Settlement = { id:string; name:string; lines:{ tons:number; pricePerTon:number; logging?:number; trucking?:number; other?:number }[] }

export default function Dashboard(){
  const [loads, setLoads] = useState<LoadTicket[]>([])
  const settlements = get<Settlement[]>('settlements', [])

  useEffect(()=>{ fetchLoads().then(setLoads) },[])

  const kpi = useMemo(()=>{
    const totalLoads = loads.length
    const totalTons = loads.reduce((s,l)=> s + (l.netTons||0), 0)
    const revenue = settlements.reduce((s,stl)=> s + stl.lines.reduce((x,l)=> x + l.tons*l.pricePerTon, 0), 0)
    const expenses = settlements.reduce((s,stl)=> s + stl.lines.reduce((x,l)=> x + (l.tons*((l.logging||0)+(l.trucking||0)+(l.other||0))), 0), 0)
    const profit = revenue - expenses
    const profitsBySettlement = settlements.map(stl => {
      const r = stl.lines.reduce((x,l)=> x + l.tons*l.pricePerTon, 0)
      const e = stl.lines.reduce((x,l)=> x + l.tons*((l.logging||0)+(l.trucking||0)+(l.other||0)), 0)
      return r - e
    })
    const last7Profit = profitsBySettlement.slice(0, 7).reverse()
    return { totalLoads, totalTons, revenue, expenses, profit, last7Profit }
  },[loads, settlements])

  return (
    <AppShell title="Dashboard">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="card-header">Quick Actions</div>
          <div className="card-body flex gap-2 flex-wrap">
            <Link to="/loads/paper-entry" className="btn">Create Load</Link>
            <Link to="/loads/import" className="btn-secondary">Import Scale Tickets</Link>
            <Link to="/loads/inbox" className="btn-secondary">Loads Inbox</Link>
            <Link to="/admin/weighbridge" className="btn-secondary">Weighbridge</Link>
            <Link to="/settlements/builder" className="btn-secondary">Settlement Builder</Link>
            <Link to="/jobs/rate-history" className="btn-secondary">Rate History</Link>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Production</div>
          <div className="card-body grid grid-cols-2 gap-3">
            <Stat label="Loads" value={kpi.totalLoads.toLocaleString()} />
            <Stat label="Tons" value={kpi.totalTons.toFixed(3)} />
          </div>
        </div>

        <div className="card">
          <div className="card-header">Financials</div>
          <div className="card-body grid grid-cols-3 gap-3">
            <Stat label="Revenue" value={`$${kpi.revenue.toFixed(2)}`} />
            <Stat label="Expenses" value={`$${kpi.expenses.toFixed(2)}`} />
            <Stat label="Profit" value={`$${kpi.profit.toFixed(2)}`} positive={kpi.profit>=0}/>
          </div>
        </div>

        <div className="card md:col-span-3">
          <div className="card-header">Profit (last 7 settlements)</div>
          <div className="card-body">
            <div className="text-xs text-slate-500 mb-2">Newer → right</div>
            <div className="text-forest-700">
              <Sparkline values={kpi.last7Profit} />
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  )
}

function Stat({label, value, positive}:{label:string; value:string; positive?:boolean}){
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-xl font-semibold ${positive===false?'text-red-600':'text-slate-800'}`}>{value}</div>
    </div>
  )
}
